/**
 * Normalise a search query: lowercase, strip punctuation, map equivalent terms.
 */

const KEYWORD_MAP: Record<string, string[]> = {
  igcse: ["igcse", "i.g.c.s.e", "cambridge igcse", "edexcel igcse", "cie igcse"],
  "a-level": ["a-level", "a level", "alevel", "a-levels", "a levels", "alevels", "advanced level"],
  ib: ["ib", "international baccalaureate"],
  ap: ["ap", "advanced placement"],
  gcse: ["gcse", "g.c.s.e"],
  economics: ["economics", "econ"],
  mathematics: ["mathematics", "maths", "math"],
  biology: ["biology", "bio"],
  chemistry: ["chemistry", "chem"],
  physics: ["physics", "phys"],
  "computer science": ["computer science", "comp sci", "cs", "computing"],
  psychology: ["psychology", "psych"],
  english: ["english", "eng"],
  history: ["history", "hist"],
  edexcel: ["edexcel", "pearson", "pearson edexcel"],
  cambridge: ["cambridge", "cie", "caie", "cambridge international"],
  aqa: ["aqa"],
  ocr: ["ocr"],
  wjec: ["wjec"],
};

export function normaliseSearch(raw: string): string {
  let q = raw.toLowerCase().replace(/[^\w\s-]/g, "").trim();

  // Replace known synonyms with canonical term
  for (const [canonical, variants] of Object.entries(KEYWORD_MAP)) {
    for (const variant of variants) {
      if (variant === canonical) continue;
      // Whole-word replacement
      const re = new RegExp(`\\b${variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
      q = q.replace(re, canonical);
    }
  }

  return q.replace(/\s+/g, " ").trim();
}

/**
 * Normalise an education level string for comparison / grouping.
 */
export function normaliseLevel(level: string): string {
  const l = level.toLowerCase().replace(/[^\w\s-]/g, "").trim();
  if (/igcse/i.test(l)) return "IGCSE";
  if (/a[\s-]?level/i.test(l)) return "A-Level";
  if (/\bib\b/i.test(l)) return "IB";
  if (/\bap\b/i.test(l)) return "AP";
  if (/gcse/i.test(l) && !/igcse/i.test(l)) return "GCSE";
  if (/uni/i.test(l)) return "University";
  return level.trim();
}

/**
 * Normalise a subject string for grouping.
 */
export function normaliseSubject(subject: string): string {
  const s = subject.toLowerCase().trim();
  for (const [canonical, variants] of Object.entries(KEYWORD_MAP)) {
    if (variants.includes(s)) {
      return canonical.charAt(0).toUpperCase() + canonical.slice(1);
    }
  }
  return subject.trim();
}
